import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import { createLoreCoverSignedUrl } from "@/features/lore/api/loreStorage";
import {
  createLoreEntry as createLoreEntryClient,
  deleteLoreEntry as deleteLoreEntryClient,
  formatLoreErrorMessage,
  getProjectLoreCapacity,
  listLoreEntries,
  loadProjectDocuments,
  LoreActionError,
  updateLoreEntry as updateLoreEntryClient,
  updateLoreEntryCanonStatus as updateLoreEntryCanonStatusClient,
  updateProjectDocument as updateProjectDocumentClient,
} from "@/features/lore/api/loreClient";
import {
  applyLoreFilters,
  buildInitialActiveDocumentKind,
  getNextVisibleSelection,
  removeLoreEntry,
  upsertLoreEntry,
} from "@/features/lore/mappers/loreMappers";
import { subscriptionStore } from "@/stores/subscriptionStore";
import type {
  CreateLoreEntryInput,
  LoreCanonStatus,
  LoreEntryFilters,
  LoreEntryView,
  LoreLimitPrompt,
  ProjectDocumentCollection,
  ProjectDocumentKind,
  ProjectDocumentView,
  ProjectLoreCapacitySummary,
  UpdateLoreEntryInput,
  UpdateProjectDocumentInput,
} from "@/types/lore";

export interface LoreWorkspaceDependencies {
  createLoreEntry: typeof createLoreEntryClient;
  deleteLoreEntry: typeof deleteLoreEntryClient;
  getProjectLoreCapacity: typeof getProjectLoreCapacity;
  listLoreEntries: typeof listLoreEntries;
  loadProjectDocuments: typeof loadProjectDocuments;
  updateLoreEntry: typeof updateLoreEntryClient;
  updateLoreEntryCanonStatus: typeof updateLoreEntryCanonStatusClient;
  updateProjectDocument: typeof updateProjectDocumentClient;
}

export interface LoreWorkspaceNotice {
  kind: "error" | "success";
  message: string;
}

export interface LoreDeleteDialogState {
  open: boolean;
  submitting: boolean;
  target: LoreEntryView | null;
}

export interface LoreWorkspaceStoreState {
  activeDocumentKind: ProjectDocumentKind | null;
  activeEntryId: string | null;
  applyGeneratedCover: (
    entryId: string,
    imagePath: string,
    creditsRemaining?: number | null,
  ) => Promise<void>;
  capacity: ProjectLoreCapacitySummary | null;
  clearLimitPrompt: () => void;
  clearNotice: () => void;
  closeDeleteDialog: () => void;
  createLoreEntry: (input: CreateLoreEntryInput) => Promise<LoreEntryView>;
  deleteDialog: LoreDeleteDialogState;
  deleteLoreEntry: () => Promise<void>;
  documents: ProjectDocumentCollection;
  filters: LoreEntryFilters;
  hydrateWorkspace: (projectId: string) => Promise<void>;
  isCreatingEntry: boolean;
  limitPrompt: LoreLimitPrompt | null;
  loreEntries: LoreEntryView[];
  notice: LoreWorkspaceNotice | null;
  openDeleteDialog: (entryId: string) => void;
  openDocument: (kind: ProjectDocumentKind) => void;
  openEntry: (entryId: string) => void;
  projectId: string | null;
  resetFilters: () => void;
  saveLoreEntry: (entryId: string, input: UpdateLoreEntryInput) => Promise<LoreEntryView>;
  saveProjectDocument: (
    kind: ProjectDocumentKind,
    input: UpdateProjectDocumentInput,
  ) => Promise<ProjectDocumentView>;
  setSearchText: (searchText: string) => void;
  setStatusFilter: (statusFilter: LoreEntryFilters["statusFilter"]) => void;
  setTypeFilter: (typeFilter: LoreEntryFilters["typeFilter"]) => void;
  startCreateEntry: () => void;
  status: "idle" | "loading" | "ready" | "error";
  updateLoreEntryCanonStatus: (
    entryId: string,
    canonStatus: LoreCanonStatus,
  ) => Promise<LoreEntryView>;
}

const defaultDependencies: LoreWorkspaceDependencies = {
  createLoreEntry: createLoreEntryClient,
  deleteLoreEntry: deleteLoreEntryClient,
  getProjectLoreCapacity,
  listLoreEntries,
  loadProjectDocuments,
  updateLoreEntry: updateLoreEntryClient,
  updateLoreEntryCanonStatus: updateLoreEntryCanonStatusClient,
  updateProjectDocument: updateProjectDocumentClient,
};

const LORE_SELECTION_STORAGE_KEY_PREFIX = "lore:selection:";

function buildEmptyDocuments(): ProjectDocumentCollection {
  return {
    governance: null,
    "story-spine": null,
  };
}

function buildDocumentCollection(documents: ProjectDocumentView[]) {
  return documents.reduce<ProjectDocumentCollection>(
    (collection, document) => ({
      ...collection,
      [document.kind]: document,
    }),
    buildEmptyDocuments(),
  );
}

function getLimitPrompt(error: unknown) {
  return error instanceof LoreActionError ? error.limitPrompt ?? null : null;
}

function readStoredSelection(projectId: string) {
  if (typeof window === "undefined") {
    return null;
  }

  const rawValue = window.localStorage.getItem(`${LORE_SELECTION_STORAGE_KEY_PREFIX}${projectId}`);
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as Pick<
      LoreWorkspaceStoreState,
      "activeDocumentKind" | "activeEntryId" | "isCreatingEntry"
    >;
  } catch {
    return null;
  }
}

function persistSelection(state: Pick<
  LoreWorkspaceStoreState,
  "activeDocumentKind" | "activeEntryId" | "isCreatingEntry" | "projectId"
>) {
  if (typeof window === "undefined" || !state.projectId) {
    return;
  }

  window.localStorage.setItem(
    `${LORE_SELECTION_STORAGE_KEY_PREFIX}${state.projectId}`,
    JSON.stringify({
      activeDocumentKind: state.activeDocumentKind,
      activeEntryId: state.activeEntryId,
      isCreatingEntry: state.isCreatingEntry,
    }),
  );
}

export function createLoreWorkspaceStore(
  deps: LoreWorkspaceDependencies = defaultDependencies,
) {
  return createStore<LoreWorkspaceStoreState>((set, get) => ({
    activeDocumentKind: buildInitialActiveDocumentKind(),
    activeEntryId: null,
    capacity: null,
    deleteDialog: {
      open: false,
      submitting: false,
      target: null,
    },
    documents: buildEmptyDocuments(),
    filters: {
      searchText: "",
      statusFilter: null,
      typeFilter: null,
    },
    isCreatingEntry: false,
    limitPrompt: null,
    loreEntries: [],
    notice: null,
    projectId: null,
    status: "idle",

    applyGeneratedCover: async (entryId, imagePath, creditsRemaining = null) => {
      const coverImageUrl = await createLoreCoverSignedUrl(imagePath).catch(() => null);

      if (typeof creditsRemaining === "number") {
        subscriptionStore.getState().setCreditBalance(creditsRemaining);
      }

      set((state) => {
        const entry = state.loreEntries.find((candidate) => candidate.id === entryId);
        if (!entry) {
          return {};
        }

        return {
          loreEntries: upsertLoreEntry(state.loreEntries, {
            ...entry,
            coverImagePath: imagePath,
            coverImageUrl,
            hasCoverImage: true,
          }),
        };
      });
    },

    hydrateWorkspace: async (projectId) => {
      const storedSelection = readStoredSelection(projectId);

      set({
        activeDocumentKind: storedSelection?.activeDocumentKind ?? buildInitialActiveDocumentKind(),
        activeEntryId: storedSelection?.activeEntryId ?? null,
        deleteDialog: {
          open: false,
          submitting: false,
          target: null,
        },
        documents: buildEmptyDocuments(),
        isCreatingEntry: storedSelection?.isCreatingEntry ?? false,
        limitPrompt: null,
        loreEntries: [],
        notice: null,
        projectId,
        status: "loading",
      });

      try {
        const [documents, loreEntries, capacity] = await Promise.all([
          deps.loadProjectDocuments(projectId),
          deps.listLoreEntries(projectId),
          deps.getProjectLoreCapacity(projectId),
        ]);

        const nextStoredSelection = readStoredSelection(projectId);
        const nextEntryId = nextStoredSelection?.activeEntryId ?? null;
        const hasSelectedEntry = nextEntryId
          ? loreEntries.some((entry) => entry.id === nextEntryId)
          : false;
        const nextDocumentKind = nextStoredSelection?.activeDocumentKind ?? buildInitialActiveDocumentKind();
        const nextIsCreatingEntry = nextStoredSelection?.isCreatingEntry ?? false;

        set({
          activeDocumentKind: hasSelectedEntry || nextIsCreatingEntry ? null : nextDocumentKind,
          activeEntryId: hasSelectedEntry ? nextEntryId : null,
          capacity,
          documents: buildDocumentCollection(documents),
          isCreatingEntry: nextIsCreatingEntry && !hasSelectedEntry,
          loreEntries,
          projectId,
          status: "ready",
        });
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatLoreErrorMessage(error),
          },
          projectId,
          status: "error",
        });
      }
    },

    openDocument: (kind) => {
      set((state) => {
        const nextState = {
          activeDocumentKind: kind,
          activeEntryId: null,
          isCreatingEntry: false,
        };
        persistSelection({
          ...state,
          ...nextState,
        });
        return nextState;
      });
    },

    openEntry: (entryId) => {
      set((state) => {
        const nextState = {
          activeDocumentKind: null,
          activeEntryId: entryId,
          isCreatingEntry: false,
        };
        persistSelection({
          ...state,
          ...nextState,
        });
        return nextState;
      });
    },

    startCreateEntry: () => {
      set((state) => {
        const nextState = {
          activeDocumentKind: null,
          activeEntryId: null,
          isCreatingEntry: true,
          limitPrompt: null,
        };
        persistSelection({
          ...state,
          ...nextState,
        });
        return nextState;
      });
    },

    setSearchText: (searchText) => {
      const state = get();
      const nextFilters = {
        ...state.filters,
        searchText,
      };
      const visibleEntries = applyLoreFilters(state.loreEntries, nextFilters);
      const nextActiveEntryId = state.isCreatingEntry
        ? state.activeEntryId
        : getNextVisibleSelection(visibleEntries, state.activeEntryId);

      set({
        activeDocumentKind:
          !state.isCreatingEntry && !nextActiveEntryId && !state.activeDocumentKind
            ? buildInitialActiveDocumentKind()
            : state.activeDocumentKind,
        activeEntryId: nextActiveEntryId,
        filters: nextFilters,
      });
    },

    setStatusFilter: (statusFilter) => {
      const state = get();
      const nextFilters = {
        ...state.filters,
        statusFilter,
      };
      const visibleEntries = applyLoreFilters(state.loreEntries, nextFilters);
      const nextActiveEntryId = state.isCreatingEntry
        ? state.activeEntryId
        : getNextVisibleSelection(visibleEntries, state.activeEntryId);

      set({
        activeDocumentKind:
          !state.isCreatingEntry && !nextActiveEntryId && !state.activeDocumentKind
            ? buildInitialActiveDocumentKind()
            : state.activeDocumentKind,
        activeEntryId: nextActiveEntryId,
        filters: nextFilters,
      });
    },

    setTypeFilter: (typeFilter) => {
      const state = get();
      const nextFilters = {
        ...state.filters,
        typeFilter,
      };
      const visibleEntries = applyLoreFilters(state.loreEntries, nextFilters);
      const nextActiveEntryId = state.isCreatingEntry
        ? state.activeEntryId
        : getNextVisibleSelection(visibleEntries, state.activeEntryId);

      set({
        activeDocumentKind:
          !state.isCreatingEntry && !nextActiveEntryId && !state.activeDocumentKind
            ? buildInitialActiveDocumentKind()
            : state.activeDocumentKind,
        activeEntryId: nextActiveEntryId,
        filters: nextFilters,
      });
    },

    resetFilters: () => {
      const state = get();
      const nextFilters = {
        searchText: "",
        statusFilter: null,
        typeFilter: null,
      };
      const visibleEntries = applyLoreFilters(state.loreEntries, nextFilters);
      const nextActiveEntryId = state.isCreatingEntry
        ? state.activeEntryId
        : getNextVisibleSelection(visibleEntries, state.activeEntryId);

      set({
        activeDocumentKind:
          !state.isCreatingEntry && !nextActiveEntryId && !state.activeDocumentKind
            ? buildInitialActiveDocumentKind()
            : state.activeDocumentKind,
        activeEntryId: nextActiveEntryId,
        filters: nextFilters,
      });
    },

    saveProjectDocument: async (kind, input) => {
      const projectId = get().projectId;

      if (!projectId) {
        throw new LoreActionError("not-found", "That project is unavailable.");
      }

      const document = await deps.updateProjectDocument(projectId, kind, input);

      set((state) => ({
        documents: {
          ...state.documents,
          [kind]: document,
        },
        notice: null,
      }));

      return document;
    },

    createLoreEntry: async (input) => {
      const projectId = get().projectId;

      if (!projectId) {
        throw new LoreActionError("not-found", "That project is unavailable.");
      }

      try {
        const entry = await deps.createLoreEntry(projectId, input);
        const capacity = await deps.getProjectLoreCapacity(projectId).catch(() => get().capacity);

        set((state) => ({
          activeDocumentKind: null,
          activeEntryId: entry.id,
          capacity,
          isCreatingEntry: false,
          limitPrompt: null,
          loreEntries: upsertLoreEntry(state.loreEntries, entry),
          notice: null,
        }));

        persistSelection({
          ...get(),
          activeDocumentKind: null,
          activeEntryId: entry.id,
          isCreatingEntry: false,
        });

        return entry;
      } catch (error) {
        set({
          limitPrompt: getLimitPrompt(error),
          notice: {
            kind: "error",
            message: formatLoreErrorMessage(error),
          },
        });
        throw error;
      }
    },

    saveLoreEntry: async (entryId, input) => {
      const entry = await deps.updateLoreEntry(entryId, input);

      set((state) => ({
        loreEntries: upsertLoreEntry(state.loreEntries, entry),
        notice: null,
      }));

      return entry;
    },

    updateLoreEntryCanonStatus: async (entryId, canonStatus) => {
      const entry = await deps.updateLoreEntryCanonStatus(entryId, canonStatus);

      set((state) => ({
        loreEntries: upsertLoreEntry(state.loreEntries, entry),
        notice: null,
      }));

      return entry;
    },

    openDeleteDialog: (entryId) => {
      const target = get().loreEntries.find((entry) => entry.id === entryId) ?? null;

      set({
        deleteDialog: {
          open: Boolean(target),
          submitting: false,
          target,
        },
      });
    },

    closeDeleteDialog: () => {
      set({
        deleteDialog: {
          open: false,
          submitting: false,
          target: null,
        },
      });
    },

    deleteLoreEntry: async () => {
      const projectId = get().projectId;
      const target = get().deleteDialog.target;

      if (!projectId || !target) {
        throw new LoreActionError("not-found", "That lore entry is unavailable.");
      }

      set((state) => ({
        deleteDialog: {
          ...state.deleteDialog,
          submitting: true,
        },
      }));

      try {
        await deps.deleteLoreEntry(target.id);
        const capacity = await deps.getProjectLoreCapacity(projectId).catch(() => get().capacity);
        const nextEntries = removeLoreEntry(get().loreEntries, target.id);

        set({
          activeDocumentKind: buildInitialActiveDocumentKind(),
          activeEntryId: null,
          capacity,
          deleteDialog: {
            open: false,
            submitting: false,
            target: null,
          },
          loreEntries: nextEntries,
          notice: null,
        });

        persistSelection({
          ...get(),
          activeDocumentKind: buildInitialActiveDocumentKind(),
          activeEntryId: null,
          isCreatingEntry: false,
        });
      } catch (error) {
        set((state) => ({
          deleteDialog: {
            ...state.deleteDialog,
            submitting: false,
          },
          notice: {
            kind: "error",
            message: formatLoreErrorMessage(error),
          },
        }));
        throw error;
      }
    },

    clearNotice: () => {
      set({ notice: null });
    },

    clearLimitPrompt: () => {
      set({ limitPrompt: null });
    },
  }));
}

export const loreWorkspaceStore = createLoreWorkspaceStore();

export function useLoreWorkspaceStore<T>(
  selector: (state: LoreWorkspaceStoreState) => T,
) {
  return useStore(loreWorkspaceStore, selector);
}

export function selectProjectDocuments(state: Pick<LoreWorkspaceStoreState, "documents">) {
  return ["governance", "story-spine"]
    .map((kind) => state.documents[kind as ProjectDocumentKind])
    .filter(Boolean) as ProjectDocumentView[];
}

export function selectVisibleLoreEntries(
  state: Pick<LoreWorkspaceStoreState, "filters" | "loreEntries">,
) {
  return applyLoreFilters(state.loreEntries, state.filters);
}

export function selectActiveProjectDocument(
  state: Pick<LoreWorkspaceStoreState, "activeDocumentKind" | "documents">,
) {
  return state.activeDocumentKind ? state.documents[state.activeDocumentKind] : null;
}

export function selectActiveLoreEntry(
  state: Pick<LoreWorkspaceStoreState, "activeEntryId" | "loreEntries">,
) {
  return state.loreEntries.find((entry) => entry.id === state.activeEntryId) ?? null;
}
